import React from "react";

import {
  Button,
  styled,
  Text,
  ExternalLink,
  useSafeSetState,
  Tag,
} from "@reside/ui";
import {PccActivityLogDto} from "@reside/reside-api-admission";

import {Spinner} from "../../../atoms/spinner";
import {downloadWorkflowPdf} from "../../../services/AdmissionsService";
import {PccModal} from "../../../atoms/pcc-modal";
import {
  colorMapActivityLogStatus,
  EventTypeEnumType,
  readableActivityLogEvent,
  readableActivityLogStatus,
} from "./pccHelpers";
import {useRetryActivity} from "../hooks";
import {WorkflowType, DocumentType} from "../../../utils/enums";
import {
  getWorkflowLabel,
  getWorkflowPacketLabel,
} from "../../../atoms/start-workflow/workflowMapping";

const {EventTypeEnum, StatusEnum, ErrorCodeEnum} = PccActivityLogDto;

export const ActivityLogEventTypeCell = ({
  eventType,
}: {
  eventType: EventTypeEnumType;
}) => (
  <Text fontSize={3} fontWeight="medium" color="primary">
    {readableActivityLogEvent(eventType)}
  </Text>
);

export const ActivityLogStatusCell = ({
  activityLog,
  isPccEnabled,
}: {
  activityLog: PccActivityLogDto;
  isPccEnabled: boolean;
}) => (
  <ActivityLogStatusWrapper>
    <Tag color={colorMapActivityLogStatus(activityLog.status)}>
      {readableActivityLogStatus(activityLog.status)}
    </Tag>
    {activityLog ? (
      <ActivityLogActionCell
        activityLog={activityLog}
        isPccEnabled={isPccEnabled}
      />
    ) : null}
  </ActivityLogStatusWrapper>
);

export const ActivityLogMessageCell = ({
  activity: {
    admissionId,
    message,
    eventType,
    status,
    documentType,
    workflowType,
  },
}: {
  activity: PccActivityLogDto;
}) => {
  const showAdmissionPacketDownload =
    eventType === EventTypeEnum.UPLOAD_ADMISSION_PACKET &&
    status === StatusEnum.SUCCESS &&
    admissionId;

  // Check if UPLOAD_INDIVIDUAL_DOCUMENT exists in enum (may not be available until backend is updated)
  const show888ArbitrationDownload =
    eventType === EventTypeEnum.UPLOAD_INDIVIDUAL_DOCUMENT &&
    status === StatusEnum.SUCCESS &&
    documentType === DocumentType.ARBITRATION &&
    admissionId;

  const showWorkflowPacketDownload =
    eventType === EventTypeEnum.UPLOAD_INDIVIDUAL_DOCUMENT &&
    status === StatusEnum.SUCCESS &&
    documentType === null &&
    workflowType !== null &&
    workflowType !== WorkflowType.ADMISSION &&
    admissionId;

  return (
    <Text
      className="activity-log-message"
      color="gray100"
      fontSize={3}
      fontWeight="medium"
    >
      {message}
      {showAdmissionPacketDownload && (
        <ExternalLink
          onClick={() =>
            downloadWorkflowPdf({workflowId: admissionId, type: "full"})
          }
          className="sharedcells-link"
        >
          <br />
          Download Admission Packet
        </ExternalLink>
      )}
      {show888ArbitrationDownload && (
        <ExternalLink
          onClick={() =>
            downloadWorkflowPdf({workflowId: admissionId, type: "arbitration"})
          }
          className="sharedcells-link"
        >
          <br />
          Download Arbitration Agreement
        </ExternalLink>
      )}
      {showWorkflowPacketDownload && (
        <ExternalLink
          onClick={() =>
            downloadWorkflowPdf({workflowId: admissionId, type: "full"})
          }
          className="sharedcells-link"
        >
          <br />
          Download {getWorkflowPacketLabel(getWorkflowLabel(workflowType))}
        </ExternalLink>
      )}
    </Text>
  );
};

export const ActivityLogActionCell = ({
  activityLog: {errorCode, id, facilityId, retryable, admissionId, eventType},
  isPccEnabled,
}: {
  activityLog: PccActivityLogDto;
  isPccEnabled: boolean;
}) => {
  const [showPccModal, setShowPccModal] = useSafeSetState(false);

  const {mutate, error, isLoading} = useRetryActivity(admissionId);

  const retryActivity = () =>
    mutate({
      activityId: id,
      facilityId,
    });

  const handleRetryClick = () =>
    errorCode === ErrorCodeEnum.ACCOUNT
      ? setShowPccModal(true)
      : retryActivity();

  const renderRetryButton = () => {
    if (!isLoading || error) {
      return (
        <Button
          color="primary"
          size="small"
          outline
          onClick={handleRetryClick}
          disabled={!isPccEnabled}
        >
          Retry
        </Button>
      );
    }

    if (isLoading) {
      return (
        <>
          <CustomSpinner>
            <Spinner />
          </CustomSpinner>
          <UploadingWrapper>
            {eventType === EventTypeEnum.CREATE_PATIENT
              ? "Creating..."
              : "Uploading..."}
          </UploadingWrapper>
        </>
      );
    }
  };

  if (retryable) {
    return (
      <>
        {renderRetryButton()}
        {showPccModal && (
          <PccModal
            noSkipModal
            facilityId={facilityId}
            admissionId={admissionId}
            onRequestClose={retry => {
              setShowPccModal(false);
              retry && retryActivity();
            }}
          />
        )}
      </>
    );
  }

  return null;
};

const ActivityLogStatusWrapper = styled.div`
  width: 200px;

  > span,
  > button.small {
    width: 100%;
  }

  > button.small {
    margin-top: 5px;
    font-size: 13px;
    padding: 4px 10px;
`;

const UploadingWrapper = styled.div`
  text-align: center;
  color: ${({theme}) => theme.color.primary};
`;

const CustomSpinner = styled.div`
  .spinner-container {
    position: static;
    transform: translate(0);
  }
`;
